import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { Timeout } from '@utils/types';

export type UseTimeoutStateProps<T> = {
  timeout?: number;
  defaultValue?: T;
};

export const useTimeoutState = <T,>({ defaultValue, timeout = 1000 }: UseTimeoutStateProps<T>) => {
  const [value, setValue] = useState(defaultValue);
  const timeoutRef = useRef<Timeout>();

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (value !== defaultValue) {
      timeoutRef.current = setTimeout(() => {
        setValue(defaultValue);
      }, timeout);
    }

    return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line
  }, [value]);

  return [value, setValue] as [T, Dispatch<SetStateAction<T>>];
};

type UseTimeoutToggleStateProps = UseTimeoutStateProps<boolean>;

export const useTimeoutToggleState = ({
  defaultValue = false,
  timeout,
}: UseTimeoutToggleStateProps = {}) => {
  const [value, setValue] = useTimeoutState({ defaultValue, timeout });

  const toggleValue = () => {
    if (value === defaultValue) {
      setValue(!value);
    }
  };

  return [value, toggleValue] as [boolean, () => void];
};
